import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { ChevronRight } from 'lucide-react';
import { useApp } from '@/context/AppContext';

const noseQuestions = [
  {
    id: 'nose_q1',
    text: 'Nariz tapada o congestión'
  },
  {
    id: 'nose_q2',
    text: 'Bloqueo u obstrucción nasal'
  },
  {
    id: 'nose_q3',
    text: 'Dificultad para respirar por la nariz'
  },
  {
    id: 'nose_q4',
    text: 'Problemas para dormir por obstrucción nasal'
  },
  {
    id: 'nose_q5',
    text: 'Al hacer ejercicio o esfuerzo, sentir que no entra suficiente aire por la nariz'
  }
];

const scaleOptions = [
  {
    value: 0,
    label: 'Nada'
  },
  {
    value: 1,
    label: 'Poco'
  },
  {
    value: 2,
    label: 'Moderado'
  },
  {
    value: 3,
    label: 'Mucho'
  },
  {
    value: 4,
    label: 'Muchísimo'
  }
];

export default function RespiracionStep() {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, updateEleccionProgress } = useApp();
  const { breathingSelfReport, noseAnswers } = state.eleccion;
  const [showNoseQuestionnaire, setShowNoseQuestionnaire] = useState(false);

  // Clear previous response when component mounts (when user reaches this screen)
  useEffect(() => {
    if (!location.state?.showNoseQuestionnaire) {
      updateEleccionProgress({ 
        breathingSelfReport: '',
        noseAnswers: {},
        noseScore: null,
        maskRecommendation: null
      });
    }
  }, []);

  // Check if we should show NOSE questionnaire on mount (when coming back from results)
  useEffect(() => {
    if (location.state?.showNoseQuestionnaire && breathingSelfReport !== 'bien') {
      setShowNoseQuestionnaire(true);
    }
  }, [location.state, breathingSelfReport]);

  // Handle header back button clicks
  useEffect(() => {
    const handleHeaderBack = () => {
      if (showNoseQuestionnaire || (breathingSelfReport && breathingSelfReport !== 'bien' && !state.eleccion.maskRecommendation)) {
        // If in NOSE questionnaire, go back to initial breathing question
        setShowNoseQuestionnaire(false);
        updateEleccionProgress({ breathingSelfReport: '' });
      } else {
        // If in initial breathing question, go back to introduccion
        navigate('/introduccion');
      }
    };

    window.addEventListener('headerBackClick', handleHeaderBack);
    return () => window.removeEventListener('headerBackClick', handleHeaderBack);
  }, [showNoseQuestionnaire, breathingSelfReport, state.eleccion.maskRecommendation, navigate, updateEleccionProgress]);

  const handleBreathingResponse = (value: string) => {
    updateEleccionProgress({ breathingSelfReport: value });

    if (value === 'bien') {
      // Set nasal recommendation immediately and go to results
      updateEleccionProgress({
        maskRecommendation: 'Nasal',
        noseScore: null
      });
      navigate('/eleccion/respiracion-resultados');
    } else if (value === 'mas_o_menos' || value === 'mal') {
      // Show nose questionnaire inline
      setShowNoseQuestionnaire(true);
    }
  };

  const handleNoseAnswer = (questionId: string, value: number) => {
    const updatedAnswers = {
      ...noseAnswers,
      [questionId]: value
    };
    updateEleccionProgress({ noseAnswers: updatedAnswers });
  };

  const completeNoseQuestionnaire = () => {
    // Calculate NOSE score
    const noseRawSum = Object.values(noseAnswers).reduce((sum, value) => sum + value, 0);
    const calculatedNoseScore = noseRawSum * 5;

    // Determine mask recommendation
    const recommendation = calculatedNoseScore >= 50 ? 'Oronasal' : 'Nasal';
    
    updateEleccionProgress({
      noseScore: calculatedNoseScore,
      maskRecommendation: recommendation
    });

    navigate('/eleccion/respiracion-resultados');
  };

  const allNoseAnswered = noseQuestions.every(q => noseAnswers[q.id] !== undefined);

  // Show nose questionnaire if user answered "mas_o_menos" or "mal" and hasn't completed it yet
  if (showNoseQuestionnaire || (breathingSelfReport && breathingSelfReport !== 'bien' && !state.eleccion.maskRecommendation)) {
    return (
      <div className="flex flex-col min-h-[calc(100vh-80px)]">
        <div className="flex-1 container px-4 py-8 pb-24">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              
              <p className="text-xl font-semibold">
                En el último mes...
              </p>
              <h1 className="text-2xl font-bold text-primary mb-2">
                ¿Qué tanto te ha molestado lo siguiente?
              </h1>
            </div>

            <Card>
              <CardContent className="p-6 space-y-8">
                {noseQuestions.map(question => (
                  <div key={question.id}>
                    <h3 className="text-lg font-bold mb-4">{question.text}</h3>
                    <RadioGroup 
                      value={noseAnswers[question.id]?.toString() || ''} 
                      onValueChange={value => handleNoseAnswer(question.id, parseInt(value))}
                    >
                      {scaleOptions.map(option => (
                        <div 
                          key={option.value} 
                          className="flex items-center space-x-2 cursor-pointer p-2 rounded-md hover:bg-muted/50 transition-colors"
                          onClick={() => handleNoseAnswer(question.id, option.value)}
                        >
                          <RadioGroupItem 
                            value={option.value.toString()} 
                            id={`${question.id}-${option.value}`} 
                          />
                          <Label htmlFor={`${question.id}-${option.value}`} className="flex-1 text-md cursor-pointer">
                            <span className="font-medium">{option.value}</span> = {option.label}
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Button className="mt-6 min-h-touch px-6 sm:px-8 gap-0 w-full text-lg" onClick={completeNoseQuestionnaire} 
              disabled={!allNoseAnswered}>
              <span className="">Ver resultados</span>
              <ChevronRight style={{ marginLeft: '2.5px', width: '1.25rem', height: '1.25rem', strokeWidth: '3px'}}/>
            </Button>
          </div>
        </div>

      </div>
    );
  }

  // Show initial breathing question
  return (
    <div className="flex flex-col min-h-[calc(100vh-80px)]">
      <div className="flex-1 container px-4 py-8 pb-24">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-primary mb-2 mx-2">
              ¿Cómo respiras por la nariz?
            </h1>
          </div>

          <Card>
            <CardContent className="p-6 space-y-6">
              <RadioGroup value={breathingSelfReport} onValueChange={handleBreathingResponse}>
                <div 
                  className="flex items-center space-x-2 cursor-pointer p-3 rounded-md hover:bg-muted/50 transition-colors"
                  onClick={() => handleBreathingResponse('bien')}
                >
                  <RadioGroupItem value="bien" id="bien" />
                  <Label htmlFor="bien" className="text-lg cursor-pointer flex-1">Bien</Label>
                </div>
                <div 
                  className="flex items-center space-x-2 cursor-pointer p-3 rounded-md hover:bg-muted/50 transition-colors"
                  onClick={() => handleBreathingResponse('mas_o_menos')}
                >
                  <RadioGroupItem value="mas_o_menos" id="mas_o_menos" />
                  <Label htmlFor="mas_o_menos" className="text-lg cursor-pointer flex-1">Más o menos</Label>
                </div>
                <div 
                  className="flex items-center space-x-2 cursor-pointer p-3 rounded-md hover:bg-muted/50 transition-colors"
                  onClick={() => handleBreathingResponse('mal')}
                >
                  <RadioGroupItem value="mal" id="mal" />
                  <Label htmlFor="mal" className="text-lg cursor-pointer flex-1">Mal</Label>
                </div>
              </RadioGroup>
            </CardContent>
          </Card>
        </div>
      </div>

    </div>
  );
}